import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  Eye,
  EyeOff,
  ExternalLink,
  LayoutTemplate,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import landingService, {
  FooterLinkKind,
  LandingContent,
  SectionConfig,
} from '../../../services/landingService';
import {
  LANDING_CACHE_KEY,
  DEFAULT_LANDING_CONTENT,
  SECTION_LABELS,
  mergeLandingContent,
  sectionAnchor,
} from '../../LandingPage/landingDefaults';
import {
  IconField,
  ImageField,
  ListEditor,
  NumberField,
  SelectField,
  TextAreaField,
  TextField,
  ToggleField,
} from './LandingFields';
import './ManageLandingPage.css';

type TabId =
  | 'sections'
  | 'nav'
  | 'hero'
  | 'about'
  | 'features'
  | 'videos'
  | 'howItWorks'
  | 'pricing'
  | 'contactSales'
  | 'custom'
  | 'footer';

const TABS: { id: TabId; label: string }[] = [
  { id: 'sections', label: 'Sections & Order' },
  { id: 'nav', label: 'Header' },
  { id: 'hero', label: 'Hero' },
  { id: 'about', label: 'Who We Are' },
  { id: 'features', label: 'Features' },
  { id: 'videos', label: 'Videos' },
  { id: 'howItWorks', label: 'How It Works' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'contactSales', label: 'Contact Sales Page' },
  { id: 'custom', label: 'Custom Sections' },
  { id: 'footer', label: 'Footer' },
];

const FOOTER_KINDS: { value: FooterLinkKind; label: string }[] = [
  { value: 'section', label: 'Scroll to a section' },
  { value: 'route', label: 'Page in this app (e.g. /login)' },
  { value: 'url', label: 'External website' },
  { value: 'terms', label: 'Terms & Conditions pop-up' },
  { value: 'text', label: 'Plain text (not clickable)' },
];

const LINK_HINT = 'Use /login for a page, #pricing to scroll to a section, or a full https:// address.';

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

// Trim the blank lines left over from editing multi-line fields.
const tidy = (content: LandingContent): LandingContent => {
  const c = clone(content);
  c.hero.slides.forEach((s) => {
    s.titleLead = s.titleLead.map((l) => l.trim()).filter(Boolean);
  });
  c.pricing.extraPlan.features = c.pricing.extraPlan.features.map((f) => f.trim()).filter(Boolean);
  return c;
};

const ManageLandingPage: React.FC = () => {
  const [content, setContent] = useState<LandingContent>(DEFAULT_LANDING_CONTENT);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [tab, setTab] = useState<TabId>('sections');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showToast = useCallback((type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    window.setTimeout(() => setToast(null), 4000);
  }, []);
  const showError = useCallback((text: string) => showToast('error', text), [showToast]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const merged = mergeLandingContent(await landingService.getAdminContent());
      setContent(merged);
      setSavedSnapshot(JSON.stringify(merged));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load landing page content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isDirty = !loading && !loadError && JSON.stringify(content) !== savedSnapshot;

  useEffect(() => {
    if (!isDirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const mutate = (fn: (draft: LandingContent) => void) =>
    setContent((prev) => {
      const draft = clone(prev);
      fn(draft);
      return draft;
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await landingService.save(tidy(content));
      const merged = mergeLandingContent(saved);
      setContent(merged);
      setSavedSnapshot(JSON.stringify(merged));
      try {
        localStorage.setItem(LANDING_CACHE_KEY, JSON.stringify(saved));
      } catch {
        // cache is only a nicety
      }
      showToast('success', 'Landing page saved. Changes are live.');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save landing page');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset the whole landing page to the original built-in content? Your edits will be lost.')) {
      return;
    }
    setSaving(true);
    try {
      await landingService.reset();
      try {
        localStorage.removeItem(LANDING_CACHE_KEY);
      } catch {
        // ignore
      }
      const fresh = mergeLandingContent(null);
      setContent(fresh);
      setSavedSnapshot(JSON.stringify(fresh));
      showToast('success', 'Landing page reset to the original content.');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to reset landing page');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Sections & order ----------
  const sectionLabel = (s: SectionConfig) =>
    s.type === 'custom'
      ? content.customSections[s.id]?.title || SECTION_LABELS.custom
      : SECTION_LABELS[s.type];

  const anchorOptions = content.sections.map((s) => ({ value: sectionAnchor(s), label: sectionLabel(s) }));

  const moveSection = (index: number, delta: number) =>
    mutate((d) => {
      const target = index + delta;
      if (target < 0 || target >= d.sections.length) return;
      [d.sections[index], d.sections[target]] = [d.sections[target], d.sections[index]];
    });

  const addCustomSection = () => {
    const id = `custom_${Date.now()}`;
    mutate((d) => {
      d.sections.push({ id, type: 'custom', enabled: true });
      d.customSections[id] = {
        title: 'New Section',
        body: 'Write your content here.',
        image: '',
        imagePosition: 'right',
        buttonText: '',
        buttonLink: '',
      };
    });
    setTab('custom');
  };

  const removeSection = (id: string) => {
    if (!window.confirm('Delete this custom section?')) return;
    mutate((d) => {
      d.sections = d.sections.filter((s) => s.id !== id);
      delete d.customSections[id];
    });
  };

  const renderSections = () => (
    <>
      <p className="mlp-intro">
        Sections appear on the landing page in this order, top to bottom. Use the arrows to move a section, and the
        eye to show or hide it. The header stays at the top and the footer at the bottom.
      </p>
      <ol className="mlp-section-list">
        {content.sections.map((s, index) => (
          <li key={s.id} className={`mlp-section-row ${s.enabled ? '' : 'is-hidden'}`}>
            <span className="mlp-section-num">{index + 1}</span>
            <span className="mlp-section-name">
              <strong>{sectionLabel(s)}</strong>
              <small>{s.enabled ? 'Visible' : 'Hidden'}</small>
            </span>
            <span className="mlp-list-item-tools">
              <button
                type="button"
                className="mlp-icon-btn"
                title={s.enabled ? 'Hide section' : 'Show section'}
                aria-label={s.enabled ? 'Hide section' : 'Show section'}
                onClick={() =>
                  mutate((d) => {
                    d.sections[index].enabled = !d.sections[index].enabled;
                  })
                }
              >
                {s.enabled ? <Eye /> : <EyeOff />}
              </button>
              <button
                type="button"
                className="mlp-icon-btn"
                onClick={() => moveSection(index, -1)}
                disabled={index === 0}
                aria-label="Move up"
              >
                <ArrowUp />
              </button>
              <button
                type="button"
                className="mlp-icon-btn"
                onClick={() => moveSection(index, 1)}
                disabled={index === content.sections.length - 1}
                aria-label="Move down"
              >
                <ArrowDown />
              </button>
              {s.type === 'custom' && (
                <button
                  type="button"
                  className="mlp-icon-btn mlp-icon-btn-danger"
                  onClick={() => removeSection(s.id)}
                  aria-label="Delete section"
                >
                  <Trash2 />
                </button>
              )}
            </span>
          </li>
        ))}
      </ol>
      <button type="button" className="mlp-btn mlp-add" onClick={addCustomSection}>
        <Plus />
        Add custom section
      </button>
    </>
  );

  // ---------- Header ----------
  const renderNav = () => (
    <>
    <h3 className="mlp-subhead">Announcement bar</h3>
    <p className="mlp-intro">
      A notice fixed across the very top of the landing page (for example during maintenance). Visitors can dismiss it
      for their visit. Switch it off here when it is no longer needed.
    </p>
    <div className="mlp-grid">
      <ToggleField
        label="Show announcement bar"
        checked={content.announcement.enabled}
        onChange={(v) => mutate((d) => void (d.announcement.enabled = v))}
      />
      <TextAreaField
        label="Announcement text"
        rows={3}
        wide
        value={content.announcement.text}
        onChange={(v) => mutate((d) => void (d.announcement.text = v))}
      />
    </div>
    <h3 className="mlp-subhead">Top bar</h3>
    <div className="mlp-grid">
      <ImageField
        label="Logo"
        value={content.nav.logo}
        onChange={(v) => mutate((d) => void (d.nav.logo = v))}
        onError={showError}
        hint="Shown in the top bar."
      />
      <TextField
        label="Logo description (alt text)"
        value={content.nav.logoAlt}
        onChange={(v) => mutate((d) => void (d.nav.logoAlt = v))}
      />
      <ToggleField
        label="Show trial button"
        checked={content.nav.showTrialButton}
        onChange={(v) => mutate((d) => void (d.nav.showTrialButton = v))}
      />
      <TextField label="Trial button text" value={content.nav.trialText} onChange={(v) => mutate((d) => void (d.nav.trialText = v))} />
      <TextField label="Trial button link" value={content.nav.trialLink} onChange={(v) => mutate((d) => void (d.nav.trialLink = v))} hint={LINK_HINT} />
      <TextField label="Login button text" value={content.nav.loginText} onChange={(v) => mutate((d) => void (d.nav.loginText = v))} />
      <TextField label="Login button link" value={content.nav.loginLink} onChange={(v) => mutate((d) => void (d.nav.loginLink = v))} hint={LINK_HINT} />
    </div>
    </>
  );

  // ---------- Hero ----------
  const renderHero = () => (
    <>
      <h3 className="mlp-subhead">Background slides</h3>
      <p className="mlp-intro">Slides rotate automatically. Each slide has its own picture and headline.</p>
      <div className="mlp-grid">
        <NumberField
          label="Seconds per slide"
          value={content.hero.slideIntervalSec}
          min={2}
          max={60}
          onChange={(v) => mutate((d) => void (d.hero.slideIntervalSec = v))}
        />
      </div>
      <ListEditor
        items={content.hero.slides}
        onChange={(items) => mutate((d) => void (d.hero.slides = items))}
        minItems={1}
        addLabel="Add slide"
        itemLabel={(s, i) => `Slide ${i + 1}${s.titleHighlight ? ` — ${s.titleHighlight}` : ''}`}
        newItem={() => ({
          image: 'builtin:logistic',
          alt: '',
          titleLead: ['Your headline'],
          titleHighlight: 'highlighted words',
          subtitle: '',
        })}
        renderItem={(s, update) => (
          <>
            <ImageField label="Background image" value={s.image} onChange={(v) => update({ image: v })} onError={showError} hint="Wide landscape photos work best (at least 1600px)." />
            <TextField label="Image description (alt text)" value={s.alt} onChange={(v) => update({ alt: v })} wide />
            <TextAreaField
              label="Headline (one line per row)"
              rows={3}
              value={s.titleLead.join('\n')}
              onChange={(v) => update({ titleLead: v.split('\n') })}
            />
            <TextField label="Highlighted words" value={s.titleHighlight} onChange={(v) => update({ titleHighlight: v })} wide />
            <TextAreaField label="Subtitle" value={s.subtitle} onChange={(v) => update({ subtitle: v })} />
          </>
        )}
      />

      <h3 className="mlp-subhead">Buttons</h3>
      <div className="mlp-grid">
        <TextField label="Main button text" value={content.hero.primaryButton.text} onChange={(v) => mutate((d) => void (d.hero.primaryButton.text = v))} />
        <TextField label="Main button link" value={content.hero.primaryButton.link} onChange={(v) => mutate((d) => void (d.hero.primaryButton.link = v))} hint={LINK_HINT} />
        <ToggleField label="Show second button" checked={content.hero.secondaryButton.enabled} onChange={(v) => mutate((d) => void (d.hero.secondaryButton.enabled = v))} />
        <TextField label="Second button text" value={content.hero.secondaryButton.text} onChange={(v) => mutate((d) => void (d.hero.secondaryButton.text = v))} />
        <TextField label="Second button link" value={content.hero.secondaryButton.link} onChange={(v) => mutate((d) => void (d.hero.secondaryButton.link = v))} hint={`${LINK_HINT} Leave empty and the button does nothing.`} />
      </div>

      <h3 className="mlp-subhead">Highlight numbers</h3>
      <p className="mlp-intro">Shown under the hero buttons, and again in the footer if enabled there.</p>
      <ListEditor
        items={content.hero.stats}
        onChange={(items) => mutate((d) => void (d.hero.stats = items))}
        addLabel="Add number"
        itemLabel={(s, i) => `${i + 1}. ${s.label || 'Number'}`}
        newItem={() => ({ icon: 'Star', number: '100+', label: 'New stat' })}
        renderItem={(s, update) => (
          <>
            <IconField value={s.icon} onChange={(v) => update({ icon: v })} />
            <TextField label="Number" value={s.number} onChange={(v) => update({ number: v })} />
            <TextField label="Label" value={s.label} onChange={(v) => update({ label: v })} />
          </>
        )}
      />

      <h3 className="mlp-subhead">Category cards (bottom of hero)</h3>
      <ListEditor
        items={content.hero.categories}
        onChange={(items) => mutate((d) => void (d.hero.categories = items))}
        addLabel="Add category card"
        itemLabel={(c, i) => `${i + 1}. ${c.title || 'Category'}`}
        newItem={() => ({ title: 'New category', image: '', link: '' })}
        renderItem={(c, update) => (
          <>
            <TextField label="Title" value={c.title} onChange={(v) => update({ title: v })} />
            <TextField label="Arrow link" value={c.link} onChange={(v) => update({ link: v })} hint={`${LINK_HINT} Empty = no action.`} />
            <ImageField label="Card image" value={c.image} onChange={(v) => update({ image: v })} onError={showError} />
          </>
        )}
      />
    </>
  );

  // ---------- About ----------
  const renderAbout = () => (
    <>
      <div className="mlp-grid">
        <TextField label="Section title" value={content.about.title} onChange={(v) => mutate((d) => void (d.about.title = v))} wide />
        <ImageField
          label="Background photo"
          value={content.about.image}
          onChange={(v) => mutate((d) => void (d.about.image = v))}
          onError={showError}
        />
        <TextField label="Photo description (alt text)" value={content.about.imageAlt} onChange={(v) => mutate((d) => void (d.about.imageAlt = v))} wide />
      </div>
      <h3 className="mlp-subhead">Paragraphs</h3>
      <ListEditor
        items={content.about.paragraphs.map((text) => ({ text }))}
        onChange={(items) => mutate((d) => void (d.about.paragraphs = items.map((i) => i.text)))}
        addLabel="Add paragraph"
        itemLabel={(_, i) => `Paragraph ${i + 1}`}
        newItem={() => ({ text: '' })}
        renderItem={(p, update) => <TextAreaField label="Text" rows={4} value={p.text} onChange={(v) => update({ text: v })} />}
      />
      <h3 className="mlp-subhead">Highlight chips</h3>
      <ListEditor
        items={content.about.chips}
        onChange={(items) => mutate((d) => void (d.about.chips = items))}
        addLabel="Add chip"
        itemLabel={(c, i) => `${i + 1}. ${c.text || 'Chip'}`}
        newItem={() => ({ icon: 'Star', text: 'New highlight' })}
        renderItem={(c, update) => (
          <>
            <IconField value={c.icon} onChange={(v) => update({ icon: v })} />
            <TextField label="Text" value={c.text} onChange={(v) => update({ text: v })} />
          </>
        )}
      />
    </>
  );

  // ---------- Features ----------
  const renderFeatures = () => (
    <>
      <div className="mlp-grid">
        <TextField label="Section title" value={content.features.title} onChange={(v) => mutate((d) => void (d.features.title = v))} />
        <TextField label="Section subtitle" value={content.features.subtitle} onChange={(v) => mutate((d) => void (d.features.subtitle = v))} />
      </div>
      <ListEditor
        items={content.features.items}
        onChange={(items) => mutate((d) => void (d.features.items = items))}
        addLabel="Add feature"
        itemLabel={(f, i) => `${i + 1}. ${f.title || 'Feature'}`}
        newItem={() => ({ icon: 'Star', title: 'New feature', description: '' })}
        renderItem={(f, update) => (
          <>
            <IconField value={f.icon} onChange={(v) => update({ icon: v })} />
            <TextField label="Title" value={f.title} onChange={(v) => update({ title: v })} />
            <TextAreaField label="Description" value={f.description} onChange={(v) => update({ description: v })} />
          </>
        )}
      />
    </>
  );

  // ---------- Videos ----------
  const renderVideos = () => (
    <>
      <div className="mlp-grid">
        <TextField label="Section title" value={content.videos.title} onChange={(v) => mutate((d) => void (d.videos.title = v))} />
        <TextField label="Section subtitle" value={content.videos.subtitle} onChange={(v) => mutate((d) => void (d.videos.subtitle = v))} />
      </div>
      <ListEditor
        items={content.videos.items}
        onChange={(items) => mutate((d) => void (d.videos.items = items))}
        addLabel="Add video"
        itemLabel={(v, i) => `${i + 1}. ${v.title || 'Video'}`}
        newItem={() => ({ title: 'New video', url: '' })}
        renderItem={(v, update) => (
          <>
            <TextField label="Title" value={v.title} onChange={(val) => update({ title: val })} />
            <TextField label="YouTube or Vimeo link" value={v.url} onChange={(val) => update({ url: val })} hint="Paste the normal watch link, for example https://www.youtube.com/watch?v=…" />
          </>
        )}
      />
    </>
  );

  // ---------- How it works ----------
  const renderHowItWorks = () => (
    <>
      <div className="mlp-grid">
        <TextField label="Section title" value={content.howItWorks.title} onChange={(v) => mutate((d) => void (d.howItWorks.title = v))} />
        <TextField label="Section subtitle" value={content.howItWorks.subtitle} onChange={(v) => mutate((d) => void (d.howItWorks.subtitle = v))} />
      </div>
      <ListEditor
        items={content.howItWorks.steps}
        onChange={(items) => mutate((d) => void (d.howItWorks.steps = items))}
        addLabel="Add step"
        itemLabel={(s, i) => `Step ${i + 1} — ${s.title || 'Untitled'}`}
        newItem={() => ({ title: 'New step', description: '' })}
        renderItem={(s, update) => (
          <>
            <TextField label="Title" value={s.title} onChange={(v) => update({ title: v })} wide />
            <TextAreaField label="Description" value={s.description} onChange={(v) => update({ description: v })} />
          </>
        )}
      />
    </>
  );

  // ---------- Pricing ----------
  const renderPricing = () => {
    const extra = content.pricing.extraPlan;
    const setExtra = (patch: Partial<typeof extra>) => mutate((d) => void (d.pricing.extraPlan = { ...d.pricing.extraPlan, ...patch }));
    return (
      <>
        <p className="mlp-intro">
          The pricing cards are managed on the <Link to="/admin/pricing">Manage Pricing</Link> page. Here you control the
          words around them.
        </p>
        <div className="mlp-grid">
          <TextField label="Section title" value={content.pricing.title} onChange={(v) => mutate((d) => void (d.pricing.title = v))} wide />
          <TextAreaField label="Section subtitle" rows={2} value={content.pricing.subtitle} onChange={(v) => mutate((d) => void (d.pricing.subtitle = v))} />
          <TextAreaField label="Note under the cards" rows={2} value={content.pricing.footnote} onChange={(v) => mutate((d) => void (d.pricing.footnote = v))} />
          <TextField label="Plan button link" value={content.pricing.ctaLink} onChange={(v) => mutate((d) => void (d.pricing.ctaLink = v))} hint={`Where every plan's button goes. ${LINK_HINT}`} />
        </div>
        <h3 className="mlp-subhead">Extra card after the plans</h3>
        <p className="mlp-intro">An always-last card that is not part of Manage Pricing — Enterprise / Channel Partner by default.</p>
        <div className="mlp-grid">
          <ToggleField label="Show this card" checked={extra.enabled} onChange={(v) => setExtra({ enabled: v })} />
          <TextField label="Name" value={extra.name} onChange={(v) => setExtra({ name: v })} />
          <TextField label="Tagline" value={extra.tagline} onChange={(v) => setExtra({ tagline: v })} />
          <TextField label="Price" value={extra.price} onChange={(v) => setExtra({ price: v })} />
          <TextField label="Period" value={extra.period} onChange={(v) => setExtra({ period: v })} />
          <TextField label="Badge" value={extra.badge} onChange={(v) => setExtra({ badge: v })} hint='Use "Channel Partner" for the highlighted enterprise style.' />
          <TextField label="Button text" value={extra.cta_text} onChange={(v) => setExtra({ cta_text: v })} />
          <TextField
            label="Button link"
            value={extra.cta_link}
            onChange={(v) => setExtra({ cta_link: v })}
            hint={`Only this card's button goes here instead of the shared plan link above — defaults to the Contact Sales page. ${LINK_HINT}`}
          />
          <TextAreaField label="Features (one per line)" rows={5} value={extra.features.join('\n')} onChange={(v) => setExtra({ features: v.split('\n') })} />
        </div>
      </>
    );
  };

  // ---------- Contact Sales page ----------
  const renderContactSales = () => (
    <>
      <p className="mlp-intro">
        This is its own page at <code>/contact-sales</code>, not a section of the landing page — it opens when
        someone clicks the Enterprise/Channel Partner card's button (set on the Pricing tab). Customers use it to
        reach a channel partner, so enter that channel partner's name, phone and email under "Channel partner contact
        details" below.
      </p>
      <div className="mlp-grid">
        <TextField label="Page heading" value={content.contactSales.subheading} onChange={(v) => mutate((d) => void (d.contactSales.subheading = v))} wide />
        <TextAreaField label="Description" rows={4} value={content.contactSales.description} onChange={(v) => mutate((d) => void (d.contactSales.description = v))} wide />
      </div>

      <h3 className="mlp-subhead">Highlights</h3>
      <ListEditor
        items={content.contactSales.highlights.map((text) => ({ text }))}
        onChange={(items) => mutate((d) => void (d.contactSales.highlights = items.map((i) => i.text)))}
        addLabel="Add highlight"
        itemLabel={(h, i) => `${i + 1}. ${h.text || 'Highlight'}`}
        newItem={() => ({ text: '' })}
        renderItem={(h, update) => <TextField label="Text" value={h.text} onChange={(v) => update({ text: v })} />}
      />

      <h3 className="mlp-subhead">Channel partner contact details</h3>
      <div className="mlp-grid">
        <TextField label="Company name" value={content.contactSales.companyName} onChange={(v) => mutate((d) => void (d.contactSales.companyName = v))} />
        <TextField label="Channel partner name" value={content.contactSales.contactName} onChange={(v) => mutate((d) => void (d.contactSales.contactName = v))} />
        <TextField label="Role / title" value={content.contactSales.contactRole} onChange={(v) => mutate((d) => void (d.contactSales.contactRole = v))} />
        <TextField label="Phone" value={content.contactSales.phone} onChange={(v) => mutate((d) => void (d.contactSales.phone = v))} hint="Shown as-is, and used for the Call Us button." />
        <TextField label="Email" value={content.contactSales.email} onChange={(v) => mutate((d) => void (d.contactSales.email = v))} hint="Used for the Email Us button." />
      </div>
    </>
  );

  // ---------- Custom sections ----------
  const renderCustom = () => {
    const customs = content.sections.filter((s) => s.type === 'custom');
    return (
      <>
        <p className="mlp-intro">
          Custom sections are extra blocks of text, with an optional picture and button. Use "Sections & Order" to place
          them anywhere on the page.
        </p>
        {customs.length === 0 && <p className="mlp-empty">No custom sections yet.</p>}
        {customs.map((s) => {
          const c = content.customSections[s.id];
          if (!c) return null;
          const set = (patch: Partial<typeof c>) =>
            mutate((d) => void (d.customSections[s.id] = { ...d.customSections[s.id], ...patch }));
          return (
            <div key={s.id} className="mlp-list-item">
              <div className="mlp-list-item-head">
                <strong>{c.title || 'Custom section'}</strong>
                <span className="mlp-list-item-tools">
                  <button type="button" className="mlp-icon-btn mlp-icon-btn-danger" onClick={() => removeSection(s.id)} aria-label="Delete section">
                    <Trash2 />
                  </button>
                </span>
              </div>
              <div className="mlp-grid">
                <TextField label="Title" value={c.title} onChange={(v) => set({ title: v })} wide />
                <TextAreaField label="Text (blank line = new paragraph)" rows={6} value={c.body} onChange={(v) => set({ body: v })} />
                <ImageField label="Picture (optional)" value={c.image} onChange={(v) => set({ image: v })} onError={showError} />
                <SelectField
                  label="Picture position"
                  value={c.imagePosition}
                  options={[
                    { value: 'right', label: 'Right of the text' },
                    { value: 'left', label: 'Left of the text' },
                  ]}
                  onChange={(v) => set({ imagePosition: v === 'left' ? 'left' : 'right' })}
                />
                <TextField label="Button text (optional)" value={c.buttonText} onChange={(v) => set({ buttonText: v })} />
                <TextField label="Button link" value={c.buttonLink} onChange={(v) => set({ buttonLink: v })} hint={LINK_HINT} />
              </div>
            </div>
          );
        })}
        <button type="button" className="mlp-btn mlp-add" onClick={addCustomSection}>
          <Plus />
          Add custom section
        </button>
      </>
    );
  };

  // ---------- Footer ----------
  const renderFooter = () => {
    const f = content.footer;
    return (
      <>
        <h3 className="mlp-subhead">Call-to-action band</h3>
        <div className="mlp-grid">
          <ToggleField label="Show the band" checked={f.cta.enabled} onChange={(v) => mutate((d) => void (d.footer.cta.enabled = v))} />
          <TextField label="Heading" value={f.cta.title} onChange={(v) => mutate((d) => void (d.footer.cta.title = v))} wide />
          <TextAreaField label="Text" rows={2} value={f.cta.text} onChange={(v) => mutate((d) => void (d.footer.cta.text = v))} />
          <TextField label="Button text" value={f.cta.buttonText} onChange={(v) => mutate((d) => void (d.footer.cta.buttonText = v))} />
          <TextField label="Button link" value={f.cta.buttonLink} onChange={(v) => mutate((d) => void (d.footer.cta.buttonLink = v))} hint={LINK_HINT} />
        </div>

        <h3 className="mlp-subhead">Brand & contact</h3>
        <div className="mlp-grid">
          <ImageField label="Footer logo" value={f.logo} onChange={(v) => mutate((d) => void (d.footer.logo = v))} onError={showError} />
          <TextAreaField label="Short description" rows={3} value={f.tagline} onChange={(v) => mutate((d) => void (d.footer.tagline = v))} />
          <TextField label="Email" value={f.email} onChange={(v) => mutate((d) => void (d.footer.email = v))} hint="Leave empty to hide." />
          <TextField label="Phone" value={f.phone} onChange={(v) => mutate((d) => void (d.footer.phone = v))} hint="Leave empty to hide." />
          <TextField label="Address" value={f.address} onChange={(v) => mutate((d) => void (d.footer.address = v))} hint="Leave empty to hide." />
        </div>

        <h3 className="mlp-subhead">Link columns</h3>
        <ListEditor
          items={f.columns}
          onChange={(items) => mutate((d) => void (d.footer.columns = items))}
          addLabel="Add column"
          itemLabel={(c, i) => `Column ${i + 1} — ${c.title || 'Untitled'}`}
          newItem={() => ({ title: 'New column', links: [] })}
          renderItem={(col, updateCol) => (
            <>
              <TextField label="Column title" value={col.title} onChange={(v) => updateCol({ title: v })} wide />
              <div className="mlp-field mlp-field-wide">
                <ListEditor
                  items={col.links}
                  onChange={(links) => updateCol({ links })}
                  addLabel="Add link"
                  itemLabel={(l, i) => `${i + 1}. ${l.label || 'Link'}`}
                  newItem={() => ({ label: 'New link', kind: 'route' as FooterLinkKind, target: '/' })}
                  renderItem={(link, update) => (
                    <>
                      <TextField label="Label" value={link.label} onChange={(v) => update({ label: v })} />
                      <SelectField
                        label="Goes to"
                        value={link.kind}
                        options={FOOTER_KINDS}
                        onChange={(v) => update({ kind: v as FooterLinkKind, target: '' })}
                      />
                      {link.kind === 'section' && (
                        <SelectField
                          label="Section"
                          value={link.target}
                          options={[{ value: '', label: 'Choose a section…' }, ...anchorOptions]}
                          onChange={(v) => update({ target: v })}
                          hint="Hidden on the page whenever that section is hidden."
                        />
                      )}
                      {(link.kind === 'route' || link.kind === 'url') && (
                        <TextField
                          label={link.kind === 'route' ? 'Page path' : 'Website address'}
                          value={link.target}
                          onChange={(v) => update({ target: v })}
                          placeholder={link.kind === 'route' ? '/login' : 'https://'}
                        />
                      )}
                    </>
                  )}
                />
              </div>
            </>
          )}
        />

        <h3 className="mlp-subhead">Bottom bar</h3>
        <div className="mlp-grid">
          <TextField label="Copyright text" value={f.copyright} onChange={(v) => mutate((d) => void (d.footer.copyright = v))} wide />
          <ToggleField label="Show the highlight numbers" checked={f.showStats} onChange={(v) => mutate((d) => void (d.footer.showStats = v))} />
        </div>
      </>
    );
  };

  const renderTab = () => {
    switch (tab) {
      case 'sections':
        return renderSections();
      case 'nav':
        return renderNav();
      case 'hero':
        return renderHero();
      case 'about':
        return renderAbout();
      case 'features':
        return renderFeatures();
      case 'videos':
        return renderVideos();
      case 'howItWorks':
        return renderHowItWorks();
      case 'pricing':
        return renderPricing();
      case 'contactSales':
        return renderContactSales();
      case 'custom':
        return renderCustom();
      case 'footer':
        return renderFooter();
      default:
        return null;
    }
  };

  const hiddenNote = (id: TabId): string | null => {
    const typeByTab: Partial<Record<TabId, string>> = {
      hero: 'hero',
      about: 'about',
      features: 'features',
      videos: 'videos',
      howItWorks: 'how_it_works',
      pricing: 'pricing',
    };
    const type = typeByTab[id];
    if (!type) return null;
    const section = content.sections.find((s) => s.type === type);
    return section && !section.enabled ? 'This section is currently hidden on the landing page.' : null;
  };

  return (
    <div className="mlp-page">
      {toast && (
        <div className={`mlp-toast ${toast.type}`} role="status">
          {toast.text}
        </div>
      )}

      <div className="mlp-header">
        <div>
          <h1 className="mlp-title">
            <span className="mlp-title-icon">
              <LayoutTemplate />
            </span>
            Landing Page
          </h1>
          <p className="mlp-subtitle">
            Edit what visitors see on the public home page — sections, pictures, text, links and footer
          </p>
        </div>
        <div className="mlp-header-actions">
          {isDirty && <span className="mlp-unsaved">Unsaved changes</span>}
          <a href="/" target="_blank" rel="noopener noreferrer" className="mlp-btn">
            <ExternalLink />
            View page
          </a>
          <button type="button" className="mlp-btn" onClick={handleReset} disabled={saving || loading}>
            <RotateCcw />
            Reset
          </button>
          <button type="button" className="mlp-btn mlp-btn-primary" onClick={handleSave} disabled={!isDirty || saving}>
            <Save />
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mlp-state">
          <RefreshCw className="mlp-spin" />
          Loading landing page...
        </div>
      ) : loadError ? (
        <div className="mlp-state mlp-state-error">
          <AlertTriangle />
          <span>{loadError}</span>
          <button type="button" className="mlp-btn mlp-btn-sm" onClick={load}>
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="mlp-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`mlp-tab ${tab === t.id ? 'active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="mlp-panel">
            {hiddenNote(tab) && <div className="mlp-note">{hiddenNote(tab)} Show it from "Sections & Order".</div>}
            {renderTab()}
          </div>
        </>
      )}
    </div>
  );
};

export default ManageLandingPage;
